// =============================================================================
// Referrer-slug capture — the single source of truth for invite attribution.
// =============================================================================
// Attribution is fragile on mobile: a WhatsApp invite link opens in WhatsApp's
// in-app browser, but Google login often hands off to Safari/Chrome — and
// localStorage/cookies do NOT survive that browser hop. The robust fix is to
// thread the slug through the OAuth `redirectUrlComplete` so CLERK carries it
// server-side across the whole flow, landing it as `/signup?ref=…` in whatever
// browser completes login. This module centralises the read/write of the slug
// across the three same-browser layers (URL param, localStorage, cookie) that
// back that up.
// =============================================================================

export const REF_KEY = "__degel_ref"

/** Read the ref slug from the current URL's `?ref=` param, if present. */
export function refFromUrl(search?: string): string | null {
  const qs = search ?? (typeof window !== "undefined" ? window.location.search : "")
  const v = new URLSearchParams(qs).get("ref")
  return v && v.trim() ? v.trim() : null
}

/** Read the ref slug from the `__degel_ref` cookie, if present. */
export function refFromCookie(): string | null {
  if (typeof document === "undefined") return null
  const m = document.cookie.match(/(?:^|;\s*)__degel_ref=([^;]+)/)
  return m ? decodeURIComponent(m[1]) : null
}

/**
 * Resolve the referrer slug from every same-browser source, most-authoritative
 * first: the URL `?ref=` (what Clerk delivers post-OAuth), then localStorage,
 * then the 30-day cookie. `search` lets callers pass a react-router location.
 */
export function resolveRef(search?: string): string | null {
  return (
    refFromUrl(search) ??
    (typeof localStorage !== "undefined" ? localStorage.getItem(REF_KEY) : null) ??
    refFromCookie() ??
    null
  )
}

/**
 * The cookie's Domain, when we can safely set one.
 *
 * ⚠️ WHY THIS EXISTS. The invite link is built from `window.location.origin`,
 * so some supporters share `www.elhadegel-friends.com/i/<slug>` and some share
 * the bare domain — and the site answers on BOTH with no redirect between them.
 * A cookie written with no Domain is HOST-ONLY, and localStorage is per-origin,
 * so a slug captured on `www.` was invisible on the bare host. Clerk returns
 * every user to the BARE domain after Google. Result: every `www` invite click
 * silently lost its referrer at the OAuth hop, and we spent weeks blaming
 * people for sharing the bare domain when it was this.
 *
 * Setting Domain to the registrable domain makes the cookie readable on both.
 * Returns "" off production (localhost, previews) — a browser silently DROPS a
 * cookie whose Domain does not cover the current host, which would make this
 * change quietly delete attribution in dev instead of fixing it.
 */
const COOKIE_DOMAIN = ((): string => {
  if (typeof window === "undefined") return ""
  const h = window.location.hostname
  return h === "elhadegel-friends.com" || h.endsWith(".elhadegel-friends.com")
    ? "; Domain=.elhadegel-friends.com"
    : ""
})()

/** Persist the slug into localStorage + a 30-day Lax cookie (both same-browser). */
export function persistRef(slug: string): void {
  try {
    localStorage.setItem(REF_KEY, slug)
  } catch {
    /* private-mode / storage disabled — the cookie + URL still carry it */
  }
  document.cookie = `${REF_KEY}=${encodeURIComponent(slug)}; Path=/${COOKIE_DOMAIN}; Max-Age=2592000; SameSite=Lax`
}

/** Clear the slug from all same-browser layers (called once signup succeeds). */
export function clearRef(): void {
  try {
    localStorage.removeItem(REF_KEY)
  } catch {
    /* ignore */
  }
  // Clear BOTH shapes: the new domain-wide cookie AND any host-only cookie left
  // over from before this fix. Deleting a cookie requires matching its Domain,
  // so one expiry call cannot remove the other — and a stale host-only cookie
  // would keep re-attributing someone who has already joined.
  document.cookie = `${REF_KEY}=; Path=/; Max-Age=0; SameSite=Lax`
  if (COOKIE_DOMAIN) {
    document.cookie = `${REF_KEY}=; Path=/${COOKIE_DOMAIN}; Max-Age=0; SameSite=Lax`
  }
}
